import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import MarketingFooter from '../components/MarketingFooter';
import { BenchmarkTrustBadge } from '../components/BenchmarkTrustBadge';
import { Motif } from '../components/ui/Motif';
import { Reveal } from '../components/ui/Reveal';

const BG = '#F5FAFC';
const INK = '#002A38';
const MUTED = '#78716C';
const TERRA = '#0076A3';
const LINE = '#ECE7DE';

const PLANS = {
  annual: [
    {
      name: 'Starter',
      planKey: 'starter',
      price: 208,
      period: 'mo',
      billedNote: '$2,500 billed annually (2 months free)',
      description: 'The basics to start hiring:',
      cta: 'Buy Now',
      ctaStyle: 'outline',
      popular: false,
      features: [
        { text: '1 user', bold: false },
        { text: 'Access to Assessments', bold: false },
        { text: 'Advanced plagiarism detection', bold: false },
        { text: 'AI anti-cheating protection', bold: false },
        { text: 'Resume parsing & JD matching', bold: false },
        { text: '50+ AI-generated questions', bold: false },
        { text: '250 credits per month', bold: false },
        { text: '100 extra credits for $20', bold: false, muted: true },
      ],
    },
    {
      name: 'Growth',
      planKey: 'professional',
      price: 417,
      period: 'mo',
      billedNote: '$5,000 billed annually (2 months free)',
      description: 'Everything in Starter plus:',
      cta: 'Buy Now',
      ctaStyle: 'filled',
      popular: true,
      features: [
        { text: '3 users', bold: false },
        { text: 'Advanced AI features', bold: true, icon: 'stars', sub: [
          'AI-Assisted Resume Scoring',
          'AI Proctoring & Face Verification',
          'Advanced Evaluation & Scorecard',
        ]},
        { text: 'AI Video Interviews', bold: false },
        { text: 'Background Verification: 50 checks/mo, then $50/check', bold: false },
        { text: '750 credits per month', bold: false },
        { text: '100 extra credits for $20', bold: false, muted: true },
        { text: 'Note: buy extra credits anytime in your billing section', bold: false, muted: true },
      ],
    },
    {
      name: 'Enterprise',
      planKey: 'enterprise',
      price: null,
      period: '',
      billedNote: '',
      description: 'Everything in Growth plus:',
      cta: 'Contact Us',
      ctaStyle: 'outline',
      popular: false,
      features: [
        { text: 'Custom users and credits', bold: false },
        { text: 'Certified assessments', bold: false },
        { text: 'Integrations:', bold: true, sub: [
          'ATS: Greenhouse, Lever, Ashby',
          'Calendar: Google & Outlook',
          '40+ more (Workday, Oracle, and more)',
        ]},
        { text: 'AI Interview templates', bold: false },
        { text: 'Background verifications', bold: false },
        { text: 'Blockchain + NFT credentials', bold: false },
        { text: 'Advanced customization', bold: false },
        { text: 'White labeling', bold: false },
        { text: 'Advanced user roles and permissions', bold: false },
        { text: 'Designated account manager', bold: false },
        { text: 'Single sign-on (SSO/SCIM)', bold: false },
        { text: 'Premium 24/7 support', bold: false },
      ],
    },
  ],
  monthly: [
    {
      name: 'Starter',
      planKey: 'starter',
      price: 250,
      period: 'mo',
      billedNote: 'Billed monthly',
      description: 'The basics to start hiring:',
      cta: 'Buy Now',
      ctaStyle: 'outline',
      popular: false,
      features: [
        { text: '1 user', bold: false },
        { text: 'Access to Assessments', bold: false },
        { text: 'Advanced plagiarism detection', bold: false },
        { text: 'AI anti-cheating protection', bold: false },
        { text: 'Resume parsing & JD matching', bold: false },
        { text: '50+ AI-generated questions', bold: false },
        { text: '250 credits per month', bold: false },
        { text: '100 extra credits for $20', bold: false, muted: true },
      ],
    },
    {
      name: 'Growth',
      planKey: 'professional',
      price: 500,
      period: 'mo',
      billedNote: 'Billed monthly',
      description: 'Everything in Starter plus:',
      cta: 'Buy Now',
      ctaStyle: 'filled',
      popular: true,
      features: [
        { text: '3 users', bold: false },
        { text: 'Advanced AI features', bold: true, icon: 'stars', sub: [
          'AI-Assisted Resume Scoring',
          'AI Proctoring & Face Verification',
          'Advanced Evaluation & Scorecard',
        ]},
        { text: 'AI Video Interviews', bold: false },
        { text: 'Background Verification: 50 checks/mo, then $50/check', bold: false },
        { text: '750 credits per month', bold: false },
        { text: '100 extra credits for $20', bold: false, muted: true },
        { text: 'Note: buy extra credits anytime in your billing section', bold: false, muted: true },
      ],
    },
    {
      name: 'Enterprise',
      planKey: 'enterprise',
      price: null,
      period: '',
      billedNote: '',
      description: 'Everything in Growth plus:',
      cta: 'Contact Us',
      ctaStyle: 'outline',
      popular: false,
      features: [
        { text: 'Custom users and credits', bold: false },
        { text: 'Certified assessments', bold: false },
        { text: 'Integrations:', bold: true, sub: [
          'ATS: Greenhouse, Lever, Ashby',
          'Calendar: Google & Outlook',
          '40+ more (Workday, Oracle, and more)',
        ]},
        { text: 'AI Interview templates', bold: false },
        { text: 'Background verifications', bold: false },
        { text: 'Blockchain + NFT credentials', bold: false },
        { text: 'Advanced customization', bold: false },
        { text: 'White labeling', bold: false },
        { text: 'Advanced user roles and permissions', bold: false },
        { text: 'Designated account manager', bold: false },
        { text: 'Single sign-on (SSO/SCIM)', bold: false },
        { text: 'Premium 24/7 support', bold: false },
      ],
    },
  ],
};

const FAQS = [
  { q: 'What are credits?', a: 'One credit is used per AI interview. Your plan includes a monthly credit allotment (Starter 250/mo, Growth 750/mo) that resets at the start of each calendar month. Need more? Buy extra credits anytime at 100 credits for $20 — purchased credits roll over and never expire.' },
  { q: 'What types of assessments and questions are included?', a: 'All plans include access to our AI-generated question library spanning multiple-choice, short answer, and coding challenges. Assessments are timed, proctored evaluations that candidates take independently — auto-generated from your job description. Growth and Enterprise plans include access to advanced question types and custom assessment creation.' },
  { q: 'How does CHIRE detect cheating or prevent candidates from using ChatGPT?', a: 'CHIRE offers comprehensive anti-cheating protection including: tab-switch and window-blur detection, copy-paste monitoring, right-click prevention, per-question time tracking, AI-generated answer detection (flags responses likely written by ChatGPT), and face presence verification via webcam proctoring. All events are logged and presented in an integrity report for recruiters.' },
  { q: 'Can we create our own custom assessments and questions?', a: 'Yes! You can upload a job description and AI will auto-generate a complete assessment, or you can create custom assessments by mixing and matching question types. You can also add, edit, or remove individual questions, adjust difficulty levels, and set custom time limits and scoring rubrics.' },
  { q: 'What are AI Video Interviews?', a: 'AI Video Interviews are collaborative video sessions where candidates answer questions via webcam with live speech-to-text transcription. The AI scores responses on technical depth, communication quality, and relevance. AI Video Interviews are available on the Growth and Enterprise plans (not included in Starter). Enterprise adds dedicated interview templates spanning Technical (system design, coding), Behavioral (teamwork, leadership), and Situational (decision-making, real-world scenarios).' },
  { q: 'Does CHIRE integrate with my Applicant Tracking System (ATS)?', a: 'CHIRE offers integrations with popular ATS platforms including Greenhouse, Lever, and Ashby, as well as calendar integrations with Google and Outlook. Integrations are available on the Enterprise plan, which supports 40+ integrations including Workday, Oracle, and custom API access with webhooks for real-time events.' },
  { q: 'How many candidates can take an assessment simultaneously?', a: 'Subscription plans have generous concurrency limits. Starter plans support up to 10 simultaneous candidates. Growth plans support up to 50 simultaneous candidates. Enterprise plans support 100 to 500+ simultaneous candidates depending on configuration.' },
  { q: 'Do you accept payment by bank transfer, wire, or ACH?', a: 'Monthly plans are payable by credit card. Annual and Enterprise plans are payable by bank transfer, wire, or ACH. Enterprise plans include Net 30 payment terms. Contact contact@consultmein.com for details.' },
  { q: 'What is Background Verification & Compliance?', a: 'Our compliance module automates the entire background verification process: AI-generated document requirements per role, automated document requests with shareable candidate links, AI-powered document validation, expiry tracking with alerts, and real-time clearance status dashboards showing who is cleared, pending, or blocked. Background Verification is available on the Growth and Enterprise plans (not included in Starter). Growth includes 50 free BGV checks per month — 1 check = 1 document/verification item within a request. Beyond your monthly allotment, extra checks are $50 each and can be purchased anytime in your Billing section; purchased checks roll over and never expire.' },
  { q: 'What level of customer support is available?', a: 'Starter plans include email support with response within 24 hours. Growth plans include priority email and chat support. Enterprise plans come with a designated account manager, onboarding sessions, Slack integration, and premium 24/7 support.' },
  { q: 'How and where is candidate data processed? Is CHIRE secure?', a: 'We take data security and privacy seriously. CHIRE uses industry-standard encryption, secure cloud infrastructure, and commercially reasonable technical and organizational measures to protect your data. AI processing is handled via trusted providers (OpenAI) with strict data handling agreements. We do not use your data to train AI models. See our Privacy Policy for full details.' },
  { q: 'Can I get a demo?', a: 'Absolutely — book a personalized walkthrough and we will show you CHIRE HIRE end to end: screening, assessments, AI interviews and compliance, mapped to your roles. To get hands-on, start with the Starter plan and add Growth or Enterprise as you scale. For a demo, contact us at contact@consultmein.com.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('annual');
  const [openFaq, setOpenFaq] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const plans = PLANS[billing];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ minHeight: '100vh', background: BG, color: INK, overflowX: 'hidden' }}>
      <PublicNav />

      {/* ─── Header ─── */}
      <section style={{ position: 'relative', textAlign: 'center', padding: 'clamp(3rem, 5vw, 4.5rem) 1.5rem 1.5rem', maxWidth: 820, margin: '0 auto' }}>
        <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: '18%', left: '4%', width: 40, color: TERRA, opacity: 0.7 }} />
        <Motif shape="blob" className="motif-float" style={{ position: 'absolute', top: '30%', right: '4%', width: 58, color: '#8FD0E8', opacity: 0.3, animationDelay: '1s' }} />
        <Reveal now>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: TERRA, marginBottom: '0.9rem' }}>Pricing</div>
          <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, marginBottom: '1rem' }}>
            Choose a plan that works for you
          </h1>
          <p style={{ fontSize: '1.0625rem', color: MUTED, maxWidth: 640, margin: '0 auto 2rem', lineHeight: 1.6 }}>
            From interns to senior engineers across every role, CHIRE can help you reduce time to hire by 50% or more and hire with confidence. Get started today!
          </p>
        </Reveal>

        <Reveal now delay={0.1}>
          {/* Billing toggle */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#F1EAE1', borderRadius: 999, padding: '0.3rem', border: `1px solid ${LINE}` }}>
              <button data-testid="billing-monthly" onClick={() => setBilling('monthly')} style={{
                padding: '0.5rem 1.5rem', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: 600, transition: 'all 0.2s',
                background: billing === 'monthly' ? INK : 'transparent',
                color: billing === 'monthly' ? '#fff' : MUTED,
              }}>Monthly</button>
              <button data-testid="billing-annual" onClick={() => setBilling('annual')} style={{
                padding: '0.5rem 1.5rem', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: 600, transition: 'all 0.2s',
                background: billing === 'annual' ? INK : 'transparent',
                color: billing === 'annual' ? '#fff' : MUTED,
              }}>Annual</button>
            </div>
            {billing === 'annual' && (
              <span style={{ background: '#E6F0E7', color: '#3F6B47', padding: '0.3rem 0.8rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700 }}>
                2 months free
              </span>
            )}
          </div>

          {billing === 'annual' && (
            <p style={{ fontSize: '0.8125rem', color: MUTED, marginTop: '0.75rem' }}>
              Hiring needs vary by month? Choose an <strong>annual plan</strong> and use your credits when you need them.
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
            <BenchmarkTrustBadge variant="dark" />
          </div>
        </Reveal>
      </section>

      {/* ─── Plans ─── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', padding: 'clamp(1rem, 3vw, 1.5rem) 1.25rem clamp(1.5rem, 3vw, 3rem)', flexWrap: 'wrap', maxWidth: 1140, margin: '0 auto', alignItems: 'stretch' }}>
        {plans.map((plan) => (
          <div
            key={plan.name}
            data-testid={`pricing-card-${plan.planKey}`}
            style={{
              background: '#fff',
              border: plan.popular ? `2px solid ${TERRA}` : `1px solid ${LINE}`,
              borderRadius: 24,
              padding: '2.25rem 1.85rem',
              flex: 1,
              minWidth: 288,
              maxWidth: 360,
              position: 'relative',
              zIndex: plan.popular ? 2 : 1,
              boxShadow: plan.popular ? '0 30px 60px -30px rgba(240, 80, 0,0.5)' : '0 8px 30px rgba(28,25,23,0.04)',
            }}
          >
            {plan.popular && (
              <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: TERRA, color: '#fff', padding: '0.2rem 0.9rem', borderRadius: 999, fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.06em' }}>MOST POPULAR</div>
            )}

            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 800, color: INK, marginBottom: '1rem' }}>
              {plan.name}
            </h3>

            <div style={{ marginBottom: '0.375rem' }}>
              {plan.price !== null ? (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                  <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: INK }}>${plan.price}</span>
                  <span style={{ fontSize: '1rem', color: MUTED }}>/{plan.period}</span>
                </div>
              ) : (
                <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.9rem', fontWeight: 800, color: INK }}>Custom Pricing</span>
              )}
            </div>
            {plan.billedNote && (
              <p style={{ fontSize: '0.8125rem', color: MUTED, marginBottom: '1.25rem' }}>{plan.billedNote}</p>
            )}

            {plan.description && (
              <p style={{ fontSize: '0.875rem', color: '#44403C', fontWeight: 600, marginBottom: '1rem' }}>{plan.description}</p>
            )}

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.75rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {plan.features.map((f, fi) => (
                <li key={`${plan.name}-f-${fi}`}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.8125rem', color: f.muted ? '#A8A29E' : '#44403C', fontWeight: f.bold ? 700 : 400 }}>
                    {!f.bold && (
                      <span style={{ flexShrink: 0, marginTop: 1, width: 18, height: 18, borderRadius: 999, background: '#FFE7D9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TERRA} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                    )}
                    {f.icon === 'stars' && (
                      <span style={{ fontSize: '0.9rem', marginRight: '0.125rem', color: TERRA }}>&#9733;</span>
                    )}
                    <span>{f.text}</span>
                  </div>
                  {f.sub && (
                    <ul style={{ listStyle: 'none', padding: '0.35rem 0 0 1.6rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {f.sub.map((s, si) => (
                        <li key={`sub-${fi}-${si}`} style={{ fontSize: '0.75rem', color: MUTED, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: TERRA, flexShrink: 0 }} />
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            <button
              data-testid={`pricing-cta-${plan.planKey}`}
              onClick={() => {
                if (plan.cta === 'Contact Us') {
                  window.location.href = 'mailto:contact@consultmein.com';
                } else {
                  const planKey = `${plan.planKey || plan.name.toLowerCase()}_${billing}`;
                  navigate(`/payment?plan=${planKey}`);
                }
              }}
              disabled={plan.cta !== 'Contact Us' && !agreed}
              style={{
                width: '100%', padding: '0.8rem', borderRadius: 999, fontWeight: 700, fontSize: '0.9375rem',
                background: plan.ctaStyle === 'filled' ? (agreed ? INK : '#D6CFC6') : 'transparent',
                color: plan.ctaStyle === 'filled' ? '#fff' : (agreed ? INK : '#A8A29E'),
                border: plan.ctaStyle === 'filled' ? 'none' : `2px solid ${agreed || plan.cta === 'Contact Us' ? INK : '#D6CFC6'}`,
                cursor: plan.cta === 'Contact Us' || agreed ? 'pointer' : 'not-allowed',
                opacity: plan.cta === 'Contact Us' || agreed ? 1 : 0.8,
                transition: 'transform 0.2s, background 0.2s',
              }}
              onMouseEnter={(e) => { if (plan.cta === 'Contact Us' || agreed) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Agreement Checkbox */}
      <div style={{ maxWidth: 700, margin: '0 auto 3.5rem', padding: '0 2rem' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            data-testid="pricing-agree-checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ width: 18, height: 18, marginTop: 2, accentColor: TERRA, cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: '0.8125rem', color: '#44403C', lineHeight: 1.6 }}>
            By signing up, you agree to our{' '}
            <a href="/legal/msa" style={{ color: TERRA, textDecoration: 'underline' }}>Master Subscription Agreement</a>
            {' '}and{' '}
            <a href="/legal/privacy" style={{ color: TERRA, textDecoration: 'underline' }}>Privacy Policy</a>.
          </span>
        </label>
      </div>

      {/* ─── FAQ ─── */}
      <div style={{ maxWidth: 780, margin: '0 auto 4rem', padding: '0 2rem' }}>
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.6rem, 3.5vw, 2.25rem)', fontWeight: 800, letterSpacing: '-0.02em', color: INK, textAlign: 'center', marginBottom: '2rem' }}>
          Frequently asked questions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {FAQS.map((faq, idx) => (
            <div
              key={`faq-${idx}`}
              data-testid={`faq-item-${idx}`}
              style={{ borderBottom: `1px solid ${LINE}`, cursor: 'pointer' }}
              onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1.2rem 0', fontWeight: 700, fontSize: '0.9375rem', color: openFaq === idx ? TERRA : INK,
              }}>
                {faq.q}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={openFaq === idx ? TERRA : MUTED} strokeWidth="2"
                  style={{ transform: openFaq === idx ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              {openFaq === idx && (
                <div style={{ paddingBottom: '1.2rem', fontSize: '0.875rem', color: MUTED, lineHeight: 1.7 }}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Bottom CTA ─── */}
      <section style={{ padding: '0 clamp(1.25rem, 4vw, 3rem) clamp(3rem, 6vw, 5rem)', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ position: 'relative', overflow: 'hidden', background: INK, borderRadius: 32, padding: 'clamp(2.5rem, 6vw, 3.75rem)', textAlign: 'center', color: '#fff' }}>
          <div className="grain-overlay" style={{ opacity: 0.25 }} />
          <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: 26, left: '9%', width: 38, color: TERRA, opacity: 0.9 }} />
          <Motif shape="blob" className="motif-float" style={{ position: 'absolute', bottom: 18, right: '9%', width: 60, color: '#A9DCF2', opacity: 0.5, animationDelay: '1s' }} />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 560, margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>
              Need a custom solution?
            </h2>
            <p style={{ fontSize: '1.0625rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1.75rem', lineHeight: 1.6 }}>
              We offer tailored plans for large enterprises with specific security, compliance, or integration requirements.
            </p>
            <button data-testid="pricing-contact-sales" onClick={() => { window.location.href = 'mailto:contact@consultmein.com'; }}
              style={{ background: TERRA, color: '#fff', borderRadius: 999, fontWeight: 700, fontSize: '1rem', padding: '0.9rem 2.5rem', border: 'none', cursor: 'pointer', transition: 'transform 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
